/** @format */

import {styled} from '@mui/material/styles';
import {Box, Button, Typography, Select, FormControl, MenuItem} from '@mui/material';

export const FormContainer = styled(Box)(() => ({
  width: '500px',
  maxWidth: '100%',
}));

export const Header = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minHeight: '48px',
  padding: theme.spacing(1, 4),
  backgroundColor: theme.palette.semantic_light?.primary.container,
}));

export const HeaderIconWrapper = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  color: theme.palette.semantic_light?.primary.main,

  '.MuiSvgIcon-root': {
    fontSize: '24px',
  },
}));

export const HeaderTitle = styled(Typography)(({theme}) => ({
  flex: 1,
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '22px',
  color: theme.palette.semantic_light?.primary.onContainer,
}));

export const CloseButton = styled(Button)(({theme}) => ({
  minWidth: 'auto',
  width: '32px',
  height: '32px',
  padding: 0,
  color: theme.palette.semantic_light?.primary.main,

  '.MuiSvgIcon-root': {
    fontSize: '20px',
  },
}));

export const SubtitleText = styled(Typography)(({theme}) => ({
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '22px',
  color: theme.palette.semantic_light?.surface.onSurface,
}));

export const Content = styled(Box)(({theme}) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.semantic_light?.surface.background,
}));

export const Divider = styled(Box)(({theme}) => ({
  width: '100%',
  height: '1px',
  backgroundColor: theme.palette.semantic_light?.surface.surface3,
  marginTop: theme.spacing(6),
}));

export const FormContent = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

export const FieldsGrid = styled(Box)(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  rowGap: theme.spacing(3),
  columnGap: theme.spacing(2),
}));

export const FullWidthField = styled(Box)(() => ({
  gridColumn: '1 / -1',
  display: 'flex',
  flexDirection: 'column',
}));

export const FieldWrapper = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: theme.spacing(6),
}));

export const FieldLabel = styled(Typography)(({theme}) => ({
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '20px',
  color: theme.palette.semantic_light?.surface.onSurface,
  paddingLeft: theme.spacing(2),
}));

export const FieldHint = styled(Typography)(({theme}) => ({
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '20px',
  color: theme.palette.semantic_light?.surface.onSurface,
  paddingLeft: theme.spacing(2),
}));

export const SubmitButton = styled(Button)(({theme}) => ({
  width: '100%',
  height: '48px',
  backgroundColor: theme.palette.semantic_light?.primary.main,
  color: theme.palette.semantic_light?.primary.on,
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '8px',
  marginTop: theme.spacing(6),

  '&:hover': {
    backgroundColor: theme.palette.semantic_light?.primary.hover,
  },

  '&.Mui-disabled': {
    backgroundColor: theme.palette.semantic_light?.disabled.disabledLow,
    color: theme.palette.semantic_light?.disabled.disabled,
  },
}));

export const InputWithIcon = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.semantic_light?.surface.onSurfaceMuted}`,
  borderRadius: '8px',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),

  '&:focus-within': {
    borderColor: theme.palette.semantic_light?.primary.main,
  },
}));

export const InputIcon = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.semantic_light?.surface.onSurfaceLight,
  fontSize: '20px',

  '.MuiSvgIcon-root': {
    fontSize: '20px',
  },
}));

export const DatePickerWrapper = styled(Box)(({theme}) => ({
  flex: 1,
  '.react-datepicker-wrapper': {
    width: '100%',
  },
  '.react-datepicker__input-container input': {
    width: '100%',
    padding: 0,
    border: 'none',
    fontSize: '14px',
    backgroundColor: 'transparent',
    color: theme.palette.semantic_light?.surface.onSurface,
    boxSizing: 'border-box',
    outline: 'none',

    '&::placeholder': {
      color: theme.palette.semantic_light?.surface.onSurfaceLight,
    },
  },
}));

export const LightFormControl = styled(FormControl)(() => ({
  flex: 1,
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

export const LightSelect = styled(Select)(({theme}) => ({
  fontSize: '14px',
  border: 'none',
  backgroundColor: 'transparent',
  color: theme.palette.semantic_light?.surface.onSurface,

  '& .MuiSelect-select': {
    padding: 0,
    paddingRight: '24px !important',
    display: 'flex',
    alignItems: 'center',
    minHeight: 'unset',
  },

  '& .MuiSelect-icon': {
    color: theme.palette.semantic_light?.surface.onSurfaceLight,
    right: 0,
    fontSize: '20px',
  },
}));

export const LIGHT_SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      backgroundColor: '#FFFFFF',
      borderRadius: '8px',
      border: '1px solid #E0E0E0',
      marginTop: '4px',
      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
      maxHeight: '240px',
    },
  },
};

export const LightInputWrapper = styled(Box)(({theme}) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',

  '& > div': {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },

  '.MuiInputBase-root': {
    border: 'none',
    backgroundColor: 'transparent',

    '& fieldset': {
      border: 'none',
    },
  },

  '.MuiInputBase-root input': {
    fontSize: '16px',
    lineHeight: '20px',
    color: theme.palette.semantic_light?.surface.onSurface,

    '&::placeholder': {
      color: theme.palette.semantic_light?.surface.onSurfaceLight,
      opacity: 1,
    },
  },
}));

export const LightMenuItem = styled(MenuItem)(({theme}) => ({
  color: theme.palette.semantic_light?.surface.onSurface,
  backgroundColor: '#FFFFFF',
  '&:hover': {
    backgroundColor: '#F0F0F0',
  },
  '&.Mui-selected': {
    backgroundColor: '#E8E8E8',
    '&:hover': {
      backgroundColor: '#E0E0E0',
    },
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#F0F0F0',
  },
}));

export const ErrorText = styled(Typography)(({theme}) => ({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '18px',
  color: theme.palette.semantic_light?.bad.main,
  textAlign: 'center',
  marginTop: theme.spacing(2),
}));
